import { createContext, useContext } from 'react';

import { Cell, Pipeline, CellViewOptions, Visualisation, PipelineOperation } from '../types';
import { CombineStep } from './combine/types';

export interface ExplorationCellContextValue {
  cell: Cell;
  cellIndex: number;
  isSelectedCell: boolean;
  isConversationPart: boolean;
  isConversationSubject: boolean;
  rowContainsConversationPart: boolean;
  pipelineWithRollback: Pipeline | null;
  setCell: (cell: Cell) => void;
  setCellViewOption: <T extends keyof CellViewOptions>(
    key: T,
    value: CellViewOptions[T] | undefined,
  ) => void;
  getCellViewOption: <T extends keyof CellViewOptions>(key: T) => CellViewOptions[T]; // Json | undefined;
  isCollapsed: boolean;
  isCollapsible: boolean;
  toggleCollapsed: () => void;
  addVisualization: (visualisation: Visualisation) => void;
  addDefaultVisualization: () => void;
  startConversation: () => void;
  endConversation: () => void;
  setVisualisation: (idx: number, visualisation: Visualisation) => void;
  removeVisualisation: (idx: number) => void;
  isTableVisible: boolean;
  hideTable: () => void;
  showTable: () => void;
  isPipelinePreviewVisible: boolean;
  isBlockTitleVisible: boolean;
  tableMode: 'table' | 'cards';
  getYaml: () => string;
  copyCell: () => void;
  addPipelineOperation: (atIndex: number, operation: PipelineOperation) => void;
  removePipelineOperation: (atIndex: number) => void;
  removeParentPipelineOperation: (atIndex: number) => void;
  updatePipelineOperation: (atIndex: number, operation: PipelineOperation) => void;
  updateParentPipelineOperation: (atIndex: number, operation: PipelineOperation) => void;
  reorderPipelineOperation: (fromIndex: number, toIndex: number) => void;
  disablePipelineOperation: (atIndex: number, disabled: boolean) => void;
  disableParentPipelineOperation: (atIndex: number, disabled: boolean) => void;
  detachPipeline: () => void;
  changeBasePipeline: (newPipeline: Pipeline) => void;
  addCombine: (items: CombineStep[]) => void;
  updateCombine: (items: CombineStep[]) => void;
  setRollbackIndex: (index: number) => void;
  clearRollback: () => void;
}

const defaultContextValue = Symbol();

export const ExplorationCellContext = createContext<
  ExplorationCellContextValue | typeof defaultContextValue
>(defaultContextValue);

export const useExplorationCellContext = () => {
  const context = useContext(ExplorationCellContext);
  if (context === defaultContextValue) {
    throw new Error(
      'useExplorationCellContext must be used within a ExplorationCellContextProvider',
    );
  }
  return context;
};
